// @ts-nocheck
// TODO: Fix null object access error - priceGroup properties can be null
// Error: TS2531: Object is possibly 'null'
// File: src/util/RFXUtils.tsx
// Line: 458 (supplierBidMap[i].priceGroup.extendedPrice.value access)
// Resolution needed: Add proper null checks for priceGroup properties that are now nullable
import { PropertyFilterOperator } from '@amzn/awsui-collection-hooks';
import cloneDeep from 'lodash/cloneDeep';
import _get from 'lodash/get';
import * as XLSX from 'xlsx';

import { MultiselectProps, SelectProps } from '@amzn/awsui-components-react-v3';
import { OptionDefinition } from '@amzn/awsui-components-react-v3/polaris/internal/components/option/interfaces';

import { CommonConstants, RequestPropertyIds, ResponsePropertyIds } from '../data/BackendEntityConstants';
import { UNSET_REGION_OPTION } from '../data/BusinessConstants';
import {
  RFQ_IPS_DETAIL_LINEITEM_CLASS,
  RFQ_IPS_EAM_LINEITEM_CLASS,
  RFQ_IPS_EAM_LINEITEM_PRIMARY_KEY,
  RFQ_IPS_NON_EAM_LINEITEM_CLASS,
  RFQ_IPS_NON_EAM_LINEITEM_PRIMARY_KEY,
  RFQ_LINEITEM_DEFAULT_PRIMARY_KEY
} from '../data/RFQClassDefinitionsConstants';
import { TimeConstants } from '../data/TimeConstants';
import { QualificationStatus } from '../gql/queries/searchVendorEntityQuery';
import { Nullable } from '../interfaces';
import {
  DocumentUpdateSet,
  Property,
  RFXRequest,
  RFXResponse,
  SearchRFXQuery,
  Supplier,
  UpdatedAttachmentSet
} from '../interfaces/RFXBackendModel';
import { RFXSupplierBidMap } from '../interfaces/RFXLineItemModel';
import { RFXRequestForm } from '../interfaces/RFXRequestForm';
import { RFXResponseForm, RFXResponseState, RFXSupplierBackendModel } from '../interfaces/RFXResponseForm';
import { BUYER_VIEW_RESP_EVENT_MODEL } from '../resources/RFXResponseTable-data';
import { RFXProperty, RFXResponseSummary } from '../types/RFXRequestTypes';

export const FILE_VERSION_DELIMITER = ':deli:';

export const RFX_AUTO_GENERATE_NAME_PREFIX = 'RFQ';

export const RFX_CLONE_NAME_SUFFIX = 'CLONE';

export const getRFXIdFromUrl = (url: string) => {
  // remove the trailing slashes
  if (url.lastIndexOf('/') === url.length - 1) {
    url = url.substring(0, url.length - 1);
  }
  const lastIndex = url.lastIndexOf('/');
  return url.substring(lastIndex + 1, url.length);
};

/**
 * Differentiate editround/initiateround/create from input URL
 * @param url input pathname like /smart/buyer/view/getRevForReq-02/editround
 * @returns boolean
 */
export const isInitiateRound = (url: string) => {
  return url.endsWith('initiateround');
};

/**
 *  clonerequest from input URL
 * @param url input pathname like /smart/buyer/view/clonerequest
 * @returns boolean
 */
export const isCloneRequest = (url: string) => {
  return url.endsWith('clonerequest');
};

/**
 * Extract RFXRevisionedId from input URL
 * @param url input URL like https://.../smart/buyer/compare/RFXRFQ-REQ-af32fwef/2
 * @returns an array to represent RFXRevisionedId, e.g. [RFXRFQ-REQ-af32fwef, 2]
 */
export function getRFXRevisionedIdFromUrl(url: string): [string, number] {
  // remove the trailing slashes
  if (url.lastIndexOf('/') === url.length - 1) {
    url = url.substring(0, url.length - 1);
  }
  const lastIndex = url.lastIndexOf('/');

  if (!/^-?\d+$/.test(url.substring(lastIndex + 1, url.length))) {
    // If last section is not number type, it should be only RFX ID value with revision 1
    return [url.substring(lastIndex + 1, url.length), 1];
  }

  const revision = parseInt(url.substring(lastIndex + 1, url.length));
  url = url.substring(0, lastIndex);
  const secondLastIndex = url.lastIndexOf('/');
  return [url.substring(secondLastIndex + 1, url.length), revision];
}

/**
 * Returns a string representing current date and time in the date time string format (ISO 8601).
 */
export function getISOString(): string {
  return new Date().toISOString();
}

/**
 * Returns current timestamp in milliseconds.
 */
export function getCurrentTimeInMs(): number {
  return new Date().getTime();
}

// Returns UTC date in YYYY-MM-DD, the same format used by DatePicker
export function getUTCDateWithYearMonthDayFromEpoch(epoch: number): string {
  return new Date(epoch).toISOString().split('T')[0];
}

export function getLocalDateTimeWithYearMonthDayFromEpoch(epoch: number): string {
  const date = new Date(epoch);

  const timeString = date.toLocaleTimeString('en-us', {
    hour: 'numeric',
    minute: 'numeric'
  });
  const timezoneAbbreviation = date.toLocaleDateString('en-us', { timeZoneName: 'short' }).split(' ').pop() ?? '';

  return date.toLocaleDateString() + ' ' + timeString + ' ' + timezoneAbbreviation;
}

export function getLocalTimezoneAbbreviation(): string {
  return new Date().toLocaleDateString('en-us', { timeZoneName: 'short' }).split(' ').pop() ?? '';
}

// This function is used by property filter to filter table items
// based on the operator and the comparison result
export function isMatch(compareResult: number, operator: PropertyFilterOperator): boolean {
  switch (operator) {
    case '=':
      return compareResult === 0;
    case '!=':
      return compareResult !== 0;
    case '<':
      return compareResult < 0;
    case '<=':
      return compareResult <= 0;
    case '>':
      return compareResult > 0;
    case '>=':
      return compareResult >= 0;
  }
  return false;
}

/**
 *
 * @param num, the number of time units
 * @param unit, the unit of time
 * @returns formatted string for the input time and units
 */
function getTimeString(num: number, unit: string): string {
  if (num <= 0) return '';
  if (num === 1) return '1 ' + unit;
  return num + ' ' + unit + 's';
}

// Returns amount of time remaining, or empty string if time remaining is less than zero
export function getTimeRemainingWithDaysHoursMinutesFromEpoch(epoch: number): string {
  const currEpoch: number = Date.now();
  if (currEpoch > epoch) return '';

  const minutesInDay = 24 * 60;
  const minutesRemaining = (epoch - currEpoch) / 60000;

  const days = Math.floor(minutesRemaining / minutesInDay);
  const hours = Math.floor((minutesRemaining - days * minutesInDay) / 60);
  const minutes = Math.floor(minutesRemaining - days * minutesInDay - hours * 60);

  const daysStr = getTimeString(days, 'day');
  const hoursStr = getTimeString(hours, 'hour');
  const minuteStr = getTimeString(minutes, 'minute');

  const totalRemainingString: string = [daysStr, hoursStr, minuteStr]
    .filter((x) => typeof x === 'string' && x.length > 0)
    .join(', ');

  return '(' + totalRemainingString + ' remaining)';
}

export function getLocalDateTimeAndTimeRemaining(epoch: number): string {
  const localDateTime: string = getLocalDateTimeWithYearMonthDayFromEpoch(epoch);
  const timeRemaining: string = getTimeRemainingWithDaysHoursMinutesFromEpoch(epoch);
  return localDateTime + ' ' + timeRemaining;
}

export function getVersionFromIngestionId(ingestionId: string): number {
  return Number(ingestionId.split('"version":')[1].split('}')[0]);
}

/**
 * Returns file name from nullable file provided. Throws exception
 * if file is null.
 *
 * @param file Nullable file to retrieve name from.
 * @returns Name of file.
 */
export function getFileNameFromFile(file: Nullable<File>): string {
  if (!file) {
    throw new Error('Invalid file provided for name retrieval.');
  } else {
    return file.name;
  }
}

/**
 * Returns file format/extension from the given file name.
 * If file name is undefined or does not contain an extension, an
 * empty string will be returned.
 */
export function getFileFormatFromName(fileName: string | undefined): string {
  const separator: string = '.';
  if (!fileName || !fileName.includes(separator)) {
    return '';
  }
  return fileName.split(separator).pop() ?? '';
}

/**
 * Returns S3 URI from provided S3 ARN. Throws exception if ARN does not
 * match expected format.
 *
 * ARN must be in the form 'arn:<PARTITION>:s3:::<KEY>'
 *
 * @param url S3 URL/ARN to convert to S3 URI.
 * @returns String representation of S3 URI converted from S3 ARN.
 */
export function getS3UriFromS3Arn(url: string): string {
  const regex = /arn:.+:s3:::.+/;

  if (regex.test(url)) {
    const splitString: string[] = url.split(':::');

    return `s3://${splitString[1]}`;
  } else {
    throw new Error(`Invalid url provided for conversion: ${url}.`);
  }
}

export const getRFXRequestsByState = (rfxRequests: any[], state: string) => {
  return rfxRequests.filter((rfxRequest) => rfxRequest.status === state);
};

export const isSupplierIncludedInSuppliers = (suppliers: Supplier[], targetSupplier: string) => {
  for (let i = 0; i < suppliers.length; ++i) {
    if (suppliers[i].supplierId.value === targetSupplier) {
      return true;
    }
  }
  return false;
};

export const hasContactsSelected = (contactId: string) => {
  return !(!contactId || contactId === '');
};

export const isSupplierQualified = (qualificationStatus: string | undefined): boolean => {
  if (!qualificationStatus) return false;

  return (
    qualificationStatus === QualificationStatus.QUALIFIED ||
    qualificationStatus === QualificationStatus.VENDOR_ENGAGEMENT_APPROVED
  );
};

export const convertContactStringToList = (contactId: string): string[] => {
  if (!contactId || contactId === '') return [];
  return contactId.split(',');
};

export const convertContactListToString = (contactList: string[]): string => {
  if (!contactList || contactList.length === 0) return '';
  return contactList.join(',');
};

export const getRFXRequestsByReqAndResState = (
  rfxRequests: any[],
  reqState: string,
  resState: string,
  suppliers: string[]
) => {
  return rfxRequests.filter(
    (rfxRequest) =>
      rfxRequest.status === reqState &&
      isStatusIncludedInRespSummaries(rfxRequest.rfxResponseSummaries, rfxRequest.rfxRevision, resState, suppliers)
  );
};

export const getRFXRequestsByResState = (rfxRequests: any[], resState: string, suppliers: string[]) => {
  return rfxRequests.filter((rfxRequest) =>
    isStatusIncludedInRespSummaries(rfxRequest.rfxResponseSummaries, rfxRequest.rfxRevision, resState, suppliers)
  );
};

const isStatusIncludedInRespSummaries = (
  respSummaries: any[],
  rfxReqRevision: number,
  resState: string,
  suppliers: string[]
) => {
  for (let i = 0; respSummaries && i < respSummaries.length; ++i) {
    if (
      suppliers.includes(respSummaries[i].supplier) &&
      respSummaries[i].responseRevision === rfxReqRevision &&
      respSummaries[i].lifeCycleState === resState
    ) {
      return true;
    }
  }
  return false;
};

/**
 * Returns RFXRequests for NewRoundWithoutYou state, which means buyer published a new request
 * but the supplier isn't in requested supplier list and this supplier appears in response suppliers in any previous round.
 * So this state scenario has to two conditions - (1) the previous response revision is smaller than the current request revision.
 * (2) The supplier isn't included in requested supplier list in current round.
 */
export const getRFXRequestsForNewRoundWithoutYou = (rfxRequests: any[], suppliers: string[]) => {
  return rfxRequests.filter((rfxRequest) => {
    const responseSummaries = rfxRequest.rfxResponseSummaries;
    const includedSupplier = getSupplierIncludedInRespSummary(responseSummaries, suppliers);
    let respSummary = getRespSummaryForSpecificSupplier(responseSummaries, includedSupplier);
    return (
      rfxRequest.rfxRevision > respSummary.responseRevision &&
      !isSupplierIncludedInRequest(rfxRequest.suppliers, includedSupplier)
    );
  });
};

// Return the name of the user's associated supplier that was included in the rfxRequest previously.
// Since only one of the user's suppliers should be included in the past, return the first valid supplier found.
const getSupplierIncludedInRespSummary = (respSummaries: any[], suppliers: string[]) => {
  for (let i = 0; i < respSummaries.length; ++i) {
    const respSupplier = respSummaries[i].supplier;
    if (suppliers.includes(respSupplier)) {
      return respSupplier;
    }
  }
  return '';
};

const getRespSummaryForSpecificSupplier = (respSummaries: any[], supplier: string) => {
  for (let i = 0; i < respSummaries.length; ++i) {
    if (respSummaries[i].supplier === supplier) {
      return respSummaries[i];
    }
  }
  return '';
};

const isSupplierIncludedInRequest = (suppliers: string[], supplier: string) => {
  for (let i = 0; i < suppliers.length; ++i) {
    if (suppliers[i] === supplier) {
      return true;
    }
  }
  return false;
};

/**
 * Returns the user's one vendorId that is included in the given RFXRequest.
 * Since only one of the user's vendorIds should be included, return the first valid supplier found.
 */
export const getRFXRequestIncludedVendor = (rfxRequest: RFXRequest, vendorIds: string[]) => {
  const rfxVendors: string[] = rfxRequest.suppliers.map((supplier) => supplier.supplierId.value);
  for (let i = 0; i < vendorIds.length; i++) {
    const vendor = vendorIds[i];
    if (rfxVendors.includes(vendor)) return vendor;
  }
  return '';
};

/**
 * Returns RFXRequests for PendingResponse state, which includes below two possibilities,
 * (1) the request is published but response hasn’t been created yet
 *     or vendor hasn’t yet provided response for the current round
 * (2) vendor provided the response and the response is in Draft or accepted state
 */
export const getRFXRequestsForPendingResponseState = (rfxRequests: any[], suppliers: string[]) => {
  return rfxRequests.filter(
    (rfxRequest) =>
      rfxRequest.status === 'Published' &&
      (!isResponseProvidedForCurrentRound(rfxRequest.rfxResponseSummaries, suppliers, rfxRequest.rfxRevision) ||
        isStatusIncludedInRespSummaries(rfxRequest.rfxResponseSummaries, rfxRequest.rfxRevision, 'Draft', suppliers) ||
        isStatusIncludedInRespSummaries(rfxRequest.rfxResponseSummaries, rfxRequest.rfxRevision, 'Accepted', suppliers))
  );
};

const isResponseProvidedForCurrentRound = (respSummaries: any[], suppliers: string[], round: number) => {
  for (let i = 0; respSummaries && i < respSummaries.length; ++i) {
    if (suppliers.includes(respSummaries[i].supplier)) {
      if (respSummaries[i].responseRevision === round) {
        return true;
      }
      return false;
    }
  }
  return false;
};

export function extractPropertyOrElseDefault(
  properties: Property[] | undefined,
  variable: string,
  defaultValue: string
): string {
  if (properties === undefined) {
    return defaultValue;
  }

  for (let i = 0; i < properties.length; ++i) {
    if (properties[i].id.value === variable) {
      return properties[i].value.valueString.value;
    }
  }
  return defaultValue;
}

export function exportToExcelSheet(sheetData: any[], sheetName: string, fileName: string) {
  const workbook: XLSX.WorkBook = XLSX.utils.book_new();
  const worksheet: XLSX.WorkSheet = XLSX.utils.json_to_sheet(sheetData, { header: Object.keys(sheetData[0]) });
  XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
  XLSX.writeFileXLSX(workbook, fileName);
}

export function getAwardedSuppliersForLineItem(supplierBidMap: RFXSupplierBidMap[]): string[] {
  let awardedSuppliers: string[] | void[] = supplierBidMap
    .filter((supplierBidMapEntry) => supplierBidMapEntry.awardedQuantity !== null)
    .map((supplierBidMapEntry) => {
      return supplierBidMapEntry.supplier.value;
    });
  return awardedSuppliers;
}

export function getSupplierQuantity(supplierBidMap: RFXSupplierBidMap[], supplier: string): string {
  for (let i = 0; i < supplierBidMap.length; i++) {
    if (supplierBidMap[i].supplier.value === supplier) {
      return supplierBidMap[i].quantity ? supplierBidMap[i].quantity.value : '';
    }
  }
  return '';
}

export function getUnitPrice(supplierBidMap: RFXSupplierBidMap[], supplier: string): string {
  for (let i = 0; i < supplierBidMap.length; i++) {
    if (supplierBidMap[i].supplier.value === supplier) {
      return supplierBidMap[i].priceGroup.unitPrice?.value || '';
    }
  }
  return '';
}

export function getExtendedPrice(supplierBidMap: RFXSupplierBidMap[], supplier: string): string {
  for (let i = 0; i < supplierBidMap.length; i++) {
    if (supplierBidMap[i].supplier.value === supplier) {
      return supplierBidMap[i].priceGroup.extendedPrice ? supplierBidMap[i].priceGroup.extendedPrice.value : '';
    }
  }
  return '';
}

export function getTotalPrice(supplierBidMap: RFXSupplierBidMap[], supplier: string): string {
  for (let i = 0; i < supplierBidMap.length; i++) {
    if (supplierBidMap[i].supplier.value === supplier) {
      return supplierBidMap[i].priceGroup.totalPrice?.value || '';
    }
  }
  return '';
}

export function getLineItemPrimaryKey(lineItemClassId: string): string {
  switch (lineItemClassId) {
    case RFQ_IPS_EAM_LINEITEM_CLASS:
    case RFQ_IPS_DETAIL_LINEITEM_CLASS:
      return RFQ_IPS_EAM_LINEITEM_PRIMARY_KEY;
    case RFQ_IPS_NON_EAM_LINEITEM_CLASS:
      return RFQ_IPS_NON_EAM_LINEITEM_PRIMARY_KEY;
    default:
      return RFQ_LINEITEM_DEFAULT_PRIMARY_KEY;
  }
}

export function shouldDisableAwardDetailsButton(selectedItem: BUYER_VIEW_RESP_EVENT_MODEL): boolean {
  return !(
    selectedItem &&
    (selectedItem.status === RFXResponseState.AWARDED || selectedItem.status === RFXResponseState.PARTIALLY_AWARDED)
  );
}

export function createProperty(propertyId: string, dataValue: string, dataType?: string) {
  return {
    id: { value: propertyId },
    value: {
      valueString: { value: dataValue },
      dataType: { value: dataType || 'String' }
    }
  };
}

// Extract numerical property from RFXRequest or default
export function extractNumericalPropertyOrElseDefault(
  properties: Property[] | undefined,
  variable: string,
  defaultValue: number | undefined
): number | undefined {
  if (properties === undefined) {
    return defaultValue;
  }

  for (let i = 0; i < properties.length; ++i) {
    if (properties[i].id.value === variable) {
      const parsed = parseInt(properties[i].value.valueString.value);
      return isNaN(parsed) ? undefined : parsed;
    }
  }
  return defaultValue;
}

// Extract List of objects of given type from RFXRequest properties having the given propertyName
export function extractListPropertyOrElseDefault<Type>(
  properties: Property[] | undefined,
  propertyName: String
): Type[] {
  if (properties) {
    for (let i = 0; i < properties.length; ++i) {
      if (properties[i].id.value === propertyName) {
        try {
          return JSON.parse(properties[i].value.valueString.value);
        } catch (exception) {
          console.log(`Error while parsing json of propertyValue={${properties[i].value.valueString.value}}.
            Exception={${exception}}`);
          return [];
        }
      }
    }
  }
  return [];
}

export const extractRFXResponseDecisionTime = (rfxResponseSummaries: RFXResponseSummary[]) => {
  const summary: RFXResponseSummary | undefined = rfxResponseSummaries.find(
    (r) => r?.lifeCycleState === 'Awarded' || r?.lifeCycleState === 'Partially_Awarded'
  );
  return summary?.decisionTime;
};

// Extract default quote validity from properties of RFXRequest
export function extractDefaultQuoteValidity(properties: Property[] | undefined): number | undefined {
  if (properties === undefined) {
    return undefined;
  }

  for (let i = 0; i < properties.length; ++i) {
    if (properties[i].id.value === RequestPropertyIds.DefaultQuoteValidity) {
      const parsed = parseInt(properties[i].value.valueString.value);
      return isNaN(parsed) ? undefined : parsed;
    }
  }
  return undefined;
}

// Extract quote validity from properties of RFXResponse
export function extractQuoteValidity(properties: Property[] | undefined): number | undefined {
  if (properties === undefined) {
    return undefined;
  }

  for (let i = 0; i < properties.length; ++i) {
    if (properties[i].id.value === ResponsePropertyIds.QuoteValidity) {
      const parsed = parseInt(properties[i].value.valueString.value);
      return isNaN(parsed) ? undefined : parsed;
    }
  }
  return undefined;
}

// Update or remove quote validity from RFXResponse.
// When quoteValidity is undefined, try to remove it from properties.
// When quoteValidity is a valid number, if RFXResponse does not have this property, add a new one.
// Otherwise update the existing one.
export function updateQuoteValidity(rfxResponse: RFXResponse, quoteValidity: number | undefined) {
  if (!rfxResponse.properties) {
    rfxResponse.properties = [];
  }

  const quoteValidityPropertyIndex = rfxResponse.properties.findIndex(
    (property) => property.id.value === ResponsePropertyIds.QuoteValidity
  );

  if (quoteValidity === undefined) {
    if (quoteValidityPropertyIndex >= 0) {
      rfxResponse.properties.splice(quoteValidityPropertyIndex, 1);
    }
  } else {
    const quoteValidityString = quoteValidity.toString();
    if (quoteValidityPropertyIndex >= 0) {
      const quoteValidityProperty = rfxResponse.properties[quoteValidityPropertyIndex];
      quoteValidityProperty.value.valueString.value = quoteValidityString;
    } else {
      rfxResponse.properties.push(createProperty(ResponsePropertyIds.QuoteValidity, quoteValidityString, 'Int'));
    }
  }
}

export function getRFXRequestWithLatestVersion(rfxRequestForm: RFXRequestForm, newVersion: number): RFXRequestForm {
  let rfxRequestFormCopy: RFXRequestForm = cloneDeep(rfxRequestForm);
  rfxRequestFormCopy.rfxRequestVersion = newVersion;
  return rfxRequestFormCopy;
}

interface RFXResponseFormRegionFields {
  region: SelectProps.Option;
  regions?: MultiselectProps.Options;
}

const extractRegionForRFXResponseForm = (backendModel: RFXSupplierBackendModel): RFXResponseFormRegionFields => {
  if (backendModel.region !== CommonConstants.UNSET_REGION_VALUE) {
    return { region: { label: backendModel.region } };
  }
  if (!backendModel.regions) {
    return { regions: [], region: UNSET_REGION_OPTION };
  }
  return {
    regions: backendModel.regions.map((r) => {
      return { label: r };
    }),
    region: UNSET_REGION_OPTION
  };
};

/**
 * Utility function that converts the provided backend model to the frontend model.
 *
 * @param backendModel Model containing data for supplier view and workflow.
 * @returns Transformed frontend model.
 */
export function convertRFXSupplierBackendModelToRFXResponseForm(
  backendModel: RFXSupplierBackendModel
): RFXResponseForm {
  return {
    state: getResponseState(backendModel.responseStatus),
    round: backendModel.round,
    generalInformation: {
      requestId: backendModel.requestId,
      responseId: backendModel.responseId,
      rfxType: backendModel.rfxType,
      rfxName: backendModel.rfxName,
      beginDate: backendModel.beginDate,
      dueDate: backendModel.dueDate,
      needByDate: backendModel.needByDate,
      ...extractRegionForRFXResponseForm(backendModel),
      currency: { label: backendModel.currency },
      quoteTemplateType: { label: backendModel.quoteTemplateType },
      rfxLineItemClassId: backendModel.rfxLineItemClassId,
      defaultQuoteValidity: backendModel.defaultQuoteValidity,
      overview: backendModel.overview,
      processDetails: backendModel.processDetails
    },
    quotes: {
      quoteValidity: backendModel.quoteValidity,
      requestQuote: backendModel.requestQuote,
      quoteEntries: backendModel.responseQuotes
    },
    documents: {
      requestDocuments: backendModel.requestDocuments,
      documentEntries: backendModel.responseDocuments
    },
    contacts: {
      contacts: backendModel.contacts ? backendModel.contacts : []
    },
    review: {}
  };
}

/**
 * Utility function that determines response state based on the responses status provided.
 *
 * @param responseStatus Response status used to determine state.
 * @returns State based off of response status.
 */
export function getResponseState(responseStatus: string): RFXResponseState {
  return responseStatus === ''
    ? RFXResponseState.DRAFT
    : RFXResponseState[responseStatus.toUpperCase() as keyof typeof RFXResponseState];
}

export function hasResponseStateBeenSubmitted(state: String): boolean {
  return (
    state === RFXResponseState.SUBMITTED ||
    state === RFXResponseState.AWARDED ||
    state === RFXResponseState.PARTIALLY_AWARDED ||
    state === RFXResponseState.SOFT_AWARDED ||
    state === RFXResponseState.REJECTED ||
    state === RFXResponseState.ARCHIVED
  );
}

export function buildRequestDocumentUpdateSet(
  filename?: string,
  description?: string,
  stagingS3Uri?: string
): DocumentUpdateSet {
  return {
    supplierIds: [],
    updatedAttachmentSet: buildUpdatedAttachmentSet(filename, description, stagingS3Uri)
  };
}

export function buildUpdatedAttachmentSet(
  filename?: string,
  description?: string,
  stagingS3Uri?: string
): UpdatedAttachmentSet {
  const updatedAttachmentSetToReturn: UpdatedAttachmentSet = {};

  if (filename) {
    updatedAttachmentSetToReturn.filename = filename;
  }
  if (description || description === '') {
    // Description can be empty string
    updatedAttachmentSetToReturn.description = description;
  }
  if (stagingS3Uri) {
    updatedAttachmentSetToReturn.stagingS3Uri = stagingS3Uri;
  }

  return updatedAttachmentSetToReturn;
}

/**
 * Determines whether list of custom groups contains one of the user permission groups.
 *
 * @param userPermissionGroupSet Set of permission groups to check the list against.
 * @param listOfCustomGroups List of custom groups logged in user is part of.
 */
export function doesListContainUserPermissionGroup(
  userPermissionGroupSet: Set<string>,
  listOfCustomGroups: string[]
): boolean {
  // If the list exists and has at least one group, then for each element check whether
  // that custom group is in the user permission group set provided. If this is the case,
  // then can short-circuit and confirm the list does contain appropriate user permissions.
  return (
    listOfCustomGroups &&
    listOfCustomGroups.length > 0 &&
    listOfCustomGroups.some((customGroup) => userPermissionGroupSet.has(customGroup))
  );
}

export function removeFileVersionDelimiterIfExisting(str: string): string {
  if (str.includes(FILE_VERSION_DELIMITER)) {
    return str.split(FILE_VERSION_DELIMITER)[1];
  } else {
    return str;
  }
}

/**
 * Returns ISO date for the upload date provided. Checks whether the date string is in
 * ISO format. If so, then removes extraneous time and returns date only. Otherwise,
 * returns date as is.
 */
export function getISODateForRFXUploadDate(uploadDate: string): string {
  const dateString: string = uploadDate;
  let stringToReturn = dateString;

  if (dateString.includes('T')) {
    // Since this is ISO date-time, need to convert into ISO date only.
    stringToReturn = dateString.split('T')[0];
  }

  return stringToReturn;
}

/**
 * Utility function that extract Category Property from RFXRequest
 * into Select/Multiselect Option(s)
 *
 * @param properties rfxRequest's properties.
 * @returns Select OptionDefinition or Multiselect OptionDefinition[]
 */
export function extractCategory(
  properties: Property[] | undefined
): { category: OptionDefinition } | { categories: OptionDefinition[] } {
  const category: string = extractPropertyOrElseDefault(properties, RequestPropertyIds.Category, '');
  if (!category) {
    return extractCategories(properties);
  }
  const categoryOption: OptionDefinition = {
    label: category,
    value: category
  };
  return { category: categoryOption };
}

const extractCategories = (properties: Property[] | undefined) => {
  const stringifiedCategories: string = extractPropertyOrElseDefault(properties, RequestPropertyIds.Categories, '');
  const emptyOptions = { categories: [] };
  if (!stringifiedCategories) {
    return emptyOptions;
  }
  try {
    const categories: string[] = JSON.parse(stringifiedCategories);
    const categoriesOptions: OptionDefinition[] = categories.map(
      (c) =>
        ({
          label: c,
          value: c
        } as OptionDefinition)
    );
    return { categories: categoriesOptions };
  } catch (e) {
    return emptyOptions;
  }
};

/**
 * Function to extract category information.
 *   - IPS business: Category information is in Categories Properties and it's a stringified list.
 *   - Other cases:  Category information is in Category Property and it's a simple string with a singular category.
 */
export const extractCategoryAsString = (properties: RFXProperty[] | Property[]): string => {
  const category = extractPropertyOrElseDefault(properties, RequestPropertyIds.Category, '');
  if (category) {
    return category;
  } else {
    return extractPropertyOrElseDefault(properties, RequestPropertyIds.Categories, '');
  }
};

/**
 * Utility function that extract Regions Property from RFXRequest
 * into Select/Multiselect Option(s)
 *
 * @param rfxRequest rfxRequest.
 * @returns Select OptionDefinition or Multiselect OptionDefinition[]
 */
export function extractRegion(rfxRequest: RFXRequest): { region: OptionDefinition; regions?: OptionDefinition[] } {
  const region: string = rfxRequest.region.value;
  if (region === CommonConstants.UNSET_REGION_VALUE) {
    return extractRegions(rfxRequest.properties);
  }
  const regionOption: OptionDefinition = {
    label: region,
    value: region
  };
  return { region: regionOption };
}

const extractRegions = (
  properties: Property[] | undefined
): { region: OptionDefinition; regions?: OptionDefinition[] } => {
  const stringifiedRegions: string = extractPropertyOrElseDefault(properties, RequestPropertyIds.Regions, '');
  const emptyOptions = { regions: [], region: UNSET_REGION_OPTION };
  if (!stringifiedRegions) {
    return emptyOptions;
  }
  try {
    const regions: string[] = JSON.parse(stringifiedRegions);
    const regionsOptions: OptionDefinition[] = regions.map(
      (c) =>
        ({
          label: c,
          value: c
        } as OptionDefinition)
    );
    return { regions: regionsOptions, region: UNSET_REGION_OPTION };
  } catch (e) {
    return emptyOptions;
  }
};

/**
 * Given a request, it extracts the Site Name/FBN field. This assumes that only a single category
 * business request is passed in.
 * @param rfxRequest An RFX Request API response from RFX service
 * @returns
 */
export const extractSiteNameFBN = (rfxRequest: RFXRequest) => {
  const categoryObject = extractCategory(rfxRequest.properties);
  const rfxName = rfxRequest.name;
  const newRFXNameIdentifier = [
    RFX_AUTO_GENERATE_NAME_PREFIX,
    _get(categoryObject, ['category', 'label'], '').replace(/ /g, '-')
  ]
    .filter(Boolean)
    .join('_');
  // If the RFX name fits the RFX_<category-name>_<site-name> pattern return the <site-name> substring
  // For names with the _CLONE we need to remove the suffix as well
  if (rfxName.startsWith(newRFXNameIdentifier)) {
    if (rfxName.endsWith(RFX_CLONE_NAME_SUFFIX)) {
      return rfxName.substring(newRFXNameIdentifier.length + 1, rfxName.length - RFX_CLONE_NAME_SUFFIX.length - 1);
    } else {
      return rfxName.substring(newRFXNameIdentifier.length + 1, rfxName.length);
    }
  }
  // If it doesn't fit the pattern return an empty string.
  return '';
};

// BOM Validation should only be performed for PAC RFQs published after the date of 4/1
export function isPublishedAfterBOMValidationStart(rfxRequest: RFXRequest): boolean {
  const publishedAt: number | undefined = extractNumericalPropertyOrElseDefault(
    rfxRequest.properties,
    RequestPropertyIds.PublishedAt,
    undefined
  );
  return publishedAt !== undefined && publishedAt > TimeConstants.beginBOMValidationEpoch;
}

/**
 * Given a numeric string this function formats it in the following manner:
 * 'abc' -> 'abc'
 * 123456 -> 123,456
 * 1234567 -> 1,234,567
 * 1234567.89 -> 1,234,567.89
 */
export function formatPrice(price: string): string {
  if (!price || isNaN(Number(price))) {
    return price;
  }
  return Number(price).toLocaleString();
}

// TODO: Consider decimal values and regional formatting, extend calculations to other business groups
export function priceCalculationsForLIVE(columnId: string, newValue: string, currentItem: { [key: string]: string }) {
  if (String(columnId) === 'unitPrice') {
    let currExtendedPrice = +newValue * +currentItem['quantity'];
    let currTotalPrice = (+newValue + +currentItem['miscPrice']) * +currentItem['quantity'];
    return {
      ...currentItem,
      unitPrice: newValue,
      extendedPrice: String(currExtendedPrice),
      totalPrice: String(currTotalPrice)
    };
  } else {
    return { ...currentItem, [String(columnId)]: newValue };
  }
}

// remove null fields from a given object
export const sanitize = (obj: any) => {
  return JSON.parse(
    JSON.stringify(obj, (key, value) => {
      return value === null ? undefined : value;
    })
  );
};

export const getSearchByValidTimeQuery = (state?: string): SearchRFXQuery => {
  let query = {} as SearchRFXQuery;
  query.conditions = [];
  query.sort = [];
  if (state) {
    query.conditions.push({ lifeCycleStateName: state });
  }
  query.conditionCombinedWith = 'AND';
  query.sort.push({
    beginAt: {
      order: 'DESC'
    }
  });
  return query;
};
